import { useEffect, useMemo, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Building2,
  FileText,
  FileSpreadsheet,
  Presentation,
  Edit3,
  Info,
  Lock,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { submitRfp, rfpStoreGet, rfpStoreClear } from "../lib/rfpApi";

const RFP_STEPS = ["Client Information", "Document Upload", "Review & Submit"];

const CLIENT_INFO_KEY = "primes4_rfp_client_info";
const FILES_KEY = "primes4_rfp_files";
const SUBMISSIONS_KEY = "primes4_rfp_submissions";

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function iconForExt(ext) {
  if (ext === "xlsx") return FileSpreadsheet;
  if (ext === "pptx") return Presentation;
  return FileText;
}

const CLIENT_FIELDS = [
  { key: "company_name", label: "Company Name", required: true },
  { key: "contact_name", label: "Contact Name", required: true },
  { key: "role", label: "Job Title / Role" },
  { key: "email", label: "Email Address", required: true },
  { key: "phone", label: "Phone Number" },
  { key: "industry", label: "Industry" },
  { key: "country", label: "Country / Region" },
];

export default function EvaluateRfpReviewPage() {
  const navigate = useNavigate();
  const [client, setClient] = useState({});
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CLIENT_INFO_KEY);
      if (raw) setClient(JSON.parse(raw) || {});
    } catch (_e) {
      /* ignore */
    }
    try {
      const raw = sessionStorage.getItem(FILES_KEY);
      if (raw) {
        const meta = JSON.parse(raw);
        if (Array.isArray(meta)) setFiles(meta);
      }
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const missingDocsError = useMemo(
    () =>
      files.length === 0
        ? "No documents uploaded. Please upload at least one document before submitting."
        : "",
    [files]
  );

  const handleSubmit = () => {
    setError("");
    const missing = [];
    if (!client.company_name?.trim()) missing.push("Company Name");
    if (!client.contact_name?.trim()) missing.push("Contact Name");
    if (!client.email?.trim()) missing.push("Email Address");
    if (files.length === 0) missing.push("at least one uploaded document");

    if (missing.length > 0) {
      setError(
        `Please provide ${missing.join(", ")} before submitting.`
      );
      return;
    }

    // Resolve actual File blobs from the in-memory store. If any blob is
    // missing (e.g., user reloaded the page and lost the blobs), tell them
    // to revisit the upload step.
    const blobs = files.map((f) => rfpStoreGet(f.id || `${f.name}-${f.size}-`));
    const missingBlobs = blobs.filter((b) => !b).length;
    if (missingBlobs > 0) {
      setError(
        "Some uploaded documents were lost after a page reload. Please go back to the Document Upload step and re-add them."
      );
      return;
    }

    setSubmitting(true);
    submitRfp({
      clientInfo: {
        company_name: client.company_name || "",
        contact_name: client.contact_name || "",
        role: client.role || "",
        email: client.email || "",
        phone: client.phone || "",
        industry: client.industry || "",
        country: client.country || "",
      },
      notes: client.notes || "",
      files: blobs,
    })
      .then((resp) => {
        // Persist a lightweight history record locally (helps the user see
        // their past submissions across reloads on the same device).
        try {
          const record = {
            id: resp.id,
            submitted_at: new Date().toISOString(),
            client,
            files,
            documents_uploaded: resp.documents_uploaded,
          };
          let history = [];
          try {
            const raw = localStorage.getItem(SUBMISSIONS_KEY);
            if (raw) history = JSON.parse(raw) || [];
          } catch (_e) {
            history = [];
          }
          history.push(record);
          localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(history));
        } catch (_e) {
          /* ignore persistence failures */
        }
        try {
          localStorage.removeItem(CLIENT_INFO_KEY);
          sessionStorage.removeItem(FILES_KEY);
          rfpStoreClear();
        } catch (_e) {
          /* ignore */
        }
        setSubmitting(false);
        setSubmitted(true);
      })
      .catch((err) => {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "Submission failed. Please try again.";
        setError(detail);
        setSubmitting(false);
      });
  };

  if (submitted) {
    return (
      <div
        data-testid="rfp-review-success-page"
        className="min-h-screen flex flex-col bg-[#F8FAFC]"
      >
        <Header />
        <main className="flex-1 flex items-start justify-center pt-12 sm:pt-16 pb-12">
          <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8 sm:p-10 text-center">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-[#ECFDF5] flex items-center justify-center border border-[#10B981]/20">
                  <CheckCircle2
                    className="w-8 h-8 text-[#10B981]"
                    strokeWidth={1.75}
                  />
                </div>
              </div>

              <h1
                data-testid="rfp-success-title"
                className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111827]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Thank You
              </h1>

              <p
                data-testid="rfp-success-message"
                className="mt-3 text-sm sm:text-base text-[#374151] leading-relaxed"
              >
                Your RFP and requirements documents have been submitted
                successfully for SmartBid analysis.
              </p>

              <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                Our consulting and AI analysis team will review the documents
                and prepare SmartBid requirements analysis and proposal
                recommendations.
              </p>

              <div className="mt-5 inline-flex items-start gap-2.5 text-left bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3">
                <Info
                  className="w-4 h-4 text-[#6D28D9] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  A PrimeS4 consultant may contact you for clarification before
                  PRIVA analysis begins.
                </span>
              </div>

              <div className="mt-7">
                <Button
                  data-testid="rfp-success-back-btn"
                  onClick={() => navigate("/consultant/smartbid")}
                  className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm"
                >
                  Back to Dashboard
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      data-testid="rfp-review-page"
      className="min-h-screen flex flex-col bg-[#F8FAFC]"
    >
      <Header />

      <StepProgress currentStep={3} total={3} labels={RFP_STEPS} />

      <main className="flex-1 pt-8 sm:pt-10 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-7">
            <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5 mb-2">
              <Sparkles
                className="w-3.5 h-3.5 text-[#025979]"
                strokeWidth={1.5}
              />
              <span className="text-xs font-medium text-[#025979]">
                Step 3 — Review &amp; Submit
              </span>
            </div>
            <p
              data-testid="rfp-review-step-indicator"
              className="text-[11px] text-[#9CA3AF] mb-4 tracking-wide"
            >
              Step 3 of 3 • Confirm and submit
            </p>
            <h1
              data-testid="rfp-review-title"
              className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Review Your RFP Analysis Request
            </h1>
            <p
              data-testid="rfp-review-subtitle"
              className="mt-2 text-[13px] text-[#4B5563] leading-relaxed max-w-2xl"
            >
              Confirm client context and uploaded documents before submitting
              for SmartBid review.
            </p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
            {/* Section 1 — Client Information */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Building2
                    className="w-4 h-4 text-[#025979]"
                    strokeWidth={1.5}
                  />
                  <h2
                    className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    Client Information
                  </h2>
                </div>
                <button
                  type="button"
                  data-testid="rfp-review-edit-client-btn"
                  onClick={() => navigate("/evaluate/rfp-client-info")}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#025979] hover:text-[#03738C] transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Edit Client Information
                </button>
              </div>

              <dl
                data-testid="rfp-review-client-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4"
              >
                {CLIENT_FIELDS.map((f) => {
                  const value = (client[f.key] || "").trim();
                  const isMissing = !value;
                  return (
                    <div
                      key={f.key}
                      data-testid={`rfp-review-client-${f.key}`}
                      className="min-w-0"
                    >
                      <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                        {f.label}
                        {f.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </dt>
                      <dd
                        className={[
                          "mt-1 text-sm break-words",
                          isMissing
                            ? f.required
                              ? "text-red-600 italic"
                              : "text-[#9CA3AF] italic"
                            : "text-[#1F2937]",
                        ].join(" ")}
                      >
                        {value || (f.required ? "Missing — required" : "—")}
                      </dd>
                    </div>
                  );
                })}

                <div
                  data-testid="rfp-review-client-notes"
                  className="sm:col-span-2 min-w-0"
                >
                  <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                    Additional Notes / Context
                  </dt>
                  <dd
                    className={[
                      "mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words",
                      client.notes?.trim()
                        ? "text-[#1F2937]"
                        : "text-[#9CA3AF] italic",
                    ].join(" ")}
                  >
                    {client.notes?.trim() || "—"}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Section 2 — Uploaded Documents */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <FileText
                    className="w-4 h-4 text-[#025979]"
                    strokeWidth={1.5}
                  />
                  <h2
                    className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    Uploaded Documents
                  </h2>
                  {files.length > 0 && (
                    <span
                      data-testid="rfp-review-file-count"
                      className="text-[11px] font-medium text-[#6B7280]"
                    >
                      ({files.length})
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="rfp-review-edit-docs-btn"
                  onClick={() => navigate("/evaluate/rfp-intake")}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#025979] hover:text-[#03738C] transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Edit Documents
                </button>
              </div>

              {files.length === 0 ? (
                <div
                  data-testid="rfp-review-no-docs"
                  className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-sm px-3.5 py-2.5"
                >
                  <AlertCircle
                    className="w-4 h-4 text-red-600 mt-0.5 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="text-xs text-red-700 leading-relaxed">
                    {missingDocsError}
                  </span>
                </div>
              ) : (
                <ul
                  data-testid="rfp-review-file-list"
                  className="divide-y divide-[#E5E7EB] border border-[#E5E7EB] rounded-sm bg-white"
                >
                  {files.map((f, idx) => {
                    const Icon = iconForExt(f.ext);
                    return (
                      <li
                        key={`${f.name}-${idx}`}
                        data-testid={`rfp-review-file-item-${f.name}`}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <div className="w-9 h-9 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0">
                          <Icon
                            className="w-4 h-4 text-[#025979]"
                            strokeWidth={1.5}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1F2937] truncate">
                            {f.name}
                          </div>
                          <div className="text-[11px] text-[#6B7280] mt-0.5">
                            {(f.ext || "FILE").toUpperCase()} ·{" "}
                            {formatBytes(f.size)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Section 3 — Privacy / AI Notice */}
            <div className="p-6 sm:p-8 space-y-3">
              <div
                data-testid="rfp-review-privacy-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Lock
                  className="w-4 h-4 text-[#025979] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  Your uploaded documents are handled securely and are only
                  available inside the authorized SmartBid workflow.
                </span>
              </div>
              <div
                data-testid="rfp-review-priva-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Info
                  className="w-4 h-4 text-[#6D28D9] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  PRIVA analysis will be initiated later by an authorized
                  PrimeS4 consultant after initial review.
                </span>
              </div>
            </div>

            {/* Submit-time validation error */}
            {error && (
              <div className="px-6 sm:px-8">
                <div
                  data-testid="rfp-review-submit-error"
                  className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-sm px-3.5 py-2.5"
                >
                  <AlertCircle
                    className="w-4 h-4 text-red-600 mt-0.5 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="text-xs text-red-700 leading-relaxed">
                    {error}
                  </span>
                </div>
              </div>
            )}

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Footer actions */}
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button
                data-testid="rfp-review-back-btn"
                variant="outline"
                onClick={() => navigate("/evaluate/rfp-intake")}
                className="rounded-sm h-11 px-6 text-sm font-medium border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Document Upload
              </Button>

              <Button
                data-testid="rfp-review-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm hover:shadow disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit RFP Analysis Request"}
                {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
